/**
 * 图片上传和转换服务
 * 处理 Blob URL、Data URL、HTTP URL 之间的转换
 * 主要用于豆包API的图生图功能
 */

class ImageUploadService {
  /**
   * 检测URL类型
   * @param {string} url - 图片URL
   * @returns {string} URL类型：'blob', 'data', 'http', 'unknown'
   */
  detectUrlType(url) {
    if (!url) return 'unknown'
    
    if (url.startsWith('blob:')) return 'blob'
    if (url.startsWith('data:')) return 'data'
    if (url.startsWith('http://') || url.startsWith('https://')) return 'http'
    
    return 'unknown'
  }

  /**
   * 将Blob URL转换为Base64 Data URL
   * @param {string} blobUrl - Blob URL
   * @returns {Promise<string>} Base64 Data URL
   */
  async blobUrlToBase64(blobUrl) {
    try {
      console.log('🔄 开始转换Blob URL到Base64...')
      
      // 1. 从Blob URL获取Blob对象
      const response = await fetch(blobUrl)
      const blob = await response.blob()
      
      console.log('📦 Blob信息:', {
        size: blob.size,
        type: blob.type
      })
      
      // 2. 将Blob转换为Base64
      const base64 = await this.blobToBase64(blob)
      
      console.log('✅ Blob URL转Base64成功，长度:', base64.length)
      return base64
    } catch (error) {
      console.error('❌ Blob URL转Base64失败:', error)
      throw new Error('Blob URL转换失败: ' + error.message)
    }
  }

  /**
   * 将Blob对象转换为Base64 Data URL
   * @param {Blob} blob - Blob对象
   * @returns {Promise<string>} Base64 Data URL (格式: data:image/png;base64,...)
   */
  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onloadend = () => {
        const result = reader.result
        console.log('✅ Blob转Base64完成')
        resolve(result)
      }
      
      reader.onerror = () => {
        console.error('❌ FileReader错误:', reader.error)
        reject(new Error('读取Blob失败'))
      }
      
      reader.readAsDataURL(blob)
    })
  }

  /**
   * 将File对象转换为Base64 Data URL
   * @param {File} file - File对象
   * @returns {Promise<string>} Base64 Data URL
   */
  async fileToBase64(file) {
    try {
      console.log('🔄 开始转换File到Base64...', {
        name: file.name,
        size: file.size,
        type: file.type
      })
      
      const base64 = await this.blobToBase64(file)
      console.log('✅ File转Base64完成')
      return base64
    } catch (error) {
      console.error('❌ File转Base64失败:', error)
      throw new Error('File转换失败: ' + error.message)
    }
  }

  /**
   * 转换图片URL为豆包API可接受的格式
   * @param {string} url - 原始URL
   * @returns {Promise<string>} 转换后的URL（Base64 Data URL或HTTP URL）
   */
  async convertToApiFormat(url) {
    if (!url) {
      throw new Error('URL不能为空')
    }

    const urlType = this.detectUrlType(url)
    console.log('🔍 检测到URL类型:', urlType, '原始URL:', url.substring(0, 50) + '...')

    switch (urlType) {
      case 'blob':
        // Blob URL需要转换为Base64
        console.log('🔄 Blob URL -> Base64')
        return await this.blobUrlToBase64(url)
      
      case 'data':
        // Data URL（Base64）可以直接使用
        console.log('✅ Data URL可直接使用')
        return url
      
      case 'http':
        // HTTP URL可以直接使用（前提是可被访问）
        console.log('✅ HTTP URL可直接使用')
        return url
      
      default:
        console.warn('⚠️ 未知URL类型，尝试作为HTTP URL使用')
        return url
    }
  }

  /**
   * 批量转换图片URL
   * @param {string|string[]} urls - 单个URL或URL数组
   * @returns {Promise<string|string[]>} 转换后的URL
   */
  async convertUrlsForApi(urls) {
    if (!urls) {
      throw new Error('URL不能为空')
    }

    // 单图模式
    if (typeof urls === 'string') {
      return await this.convertToApiFormat(urls)
    }

    // 多图模式
    if (Array.isArray(urls)) {
      console.log(`🔄 批量转换 ${urls.length} 张图片...`)
      const converted = await Promise.all(
        urls.map(url => this.convertToApiFormat(url))
      )
      console.log(`✅ 批量转换完成`)
      return converted
    }

    throw new Error('无效的URL格式')
  }

  /**
   * 压缩图片（如果过大）
   * @param {Blob} blob - 图片Blob
   * @param {number} maxSizeMB - 最大大小（MB）
   * @returns {Promise<Blob>} 压缩后的Blob
   */
  async compressImage(blob, maxSizeMB = 5) {
    const maxBytes = maxSizeMB * 1024 * 1024
    
    // 如果已经小于限制，直接返回
    if (blob.size <= maxBytes) {
      console.log('✅ 图片大小合适，无需压缩')
      return blob
    }

    console.log(`🔄 压缩图片: ${(blob.size / 1024 / 1024).toFixed(2)}MB -> 目标 ${maxSizeMB}MB`)

    return new Promise((resolve, reject) => {
      const img = new Image()
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      img.onload = () => {
        // 计算压缩比例
        const scaleFactor = Math.sqrt(maxBytes / blob.size)
        canvas.width = img.width * scaleFactor
        canvas.height = img.height * scaleFactor

        // 绘制压缩后的图片
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

        // 转换为Blob
        canvas.toBlob((compressedBlob) => {
          if (compressedBlob) {
            console.log(`✅ 压缩完成: ${(compressedBlob.size / 1024 / 1024).toFixed(2)}MB`)
            resolve(compressedBlob)
          } else {
            reject(new Error('压缩失败'))
          }
        }, blob.type || 'image/jpeg', 0.8)
      }

      img.onerror = () => reject(new Error('加载图片失败'))
      img.src = URL.createObjectURL(blob)
    })
  }

  /**
   * 验证图片格式和大小
   * @param {Blob} blob - 图片Blob
   * @returns {{valid: boolean, error: string}}
   */
  validateImage(blob) {
    // 检查文件类型
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg']
    if (!validTypes.includes(blob.type)) {
      return {
        valid: false,
        error: `不支持的图片格式: ${blob.type}，仅支持 JPEG、PNG`
      }
    }

    // 检查文件大小（10MB限制）
    const maxSize = 10 * 1024 * 1024
    if (blob.size > maxSize) {
      return {
        valid: false,
        error: `图片过大: ${(blob.size / 1024 / 1024).toFixed(2)}MB，最大支持 10MB`
      }
    }

    return { valid: true, error: null }
  }

  /**
   * 从URL下载图片并转换为Blob
   * @param {string} url - 图片URL
   * @returns {Promise<Blob>}
   */
  async downloadImage(url) {
    try {
      console.log('📥 下载图片:', url.substring(0, 50) + '...')
      
      const response = await fetch(url, {
        mode: 'cors',
        cache: 'no-cache'
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const blob = await response.blob()
      console.log('✅ 图片下载完成:', {
        size: blob.size,
        type: blob.type
      })

      return blob
    } catch (error) {
      console.error('❌ 下载图片失败:', error)
      throw new Error('下载图片失败: ' + error.message)
    }
  }
}

// 导出单例
export default new ImageUploadService()

